import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid } from '@/components/KstDataGrid';
import { ConfirmModal } from '@/components/ConfirmModal';
import { navigateToCaseReport, navigateToNewCaseReport } from '@/services/navigation';
import { CaseRpt } from '@/services/case-report-service';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import moment from 'moment';
import { currentUserHasPermission } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';

import { PendingCaseReport } from '@/models/legacy/index';
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const canCurrentUserAccessAnyApprovedCaseReports = _wg('canCurrentUserAccessAnyApprovedCaseReports');
const canCurrentUserAccessCaseReport = _wg('canCurrentUserAccessCaseReport');
const displayNocValuesForGridTemplate = _wg('displayNocValuesForGridTemplate');
const openPage = _wg('openPage');


interface CaseReportListScreenProps {
  isMobile?: boolean;
}

export function CaseReportListScreen({ isMobile = false }: CaseReportListScreenProps) {
  const { theme } = useTheme();

  const canAccessAnyApproved = useMemo(() => canCurrentUserAccessAnyApprovedCaseReports(), []);
  const showToggleButton = useMemo(() => currentUserHasPermission(AccessAuthorities.ROLE_ACCESS_PENDING_CASE_REPORTS), []);

  // Pending Submission state
  const [pendingSubmissionRows, setPendingSubmissionRows] = useState<any>({ entries: null });
  const [pendingSubmissionPage, setPendingSubmissionPage] = useState(1);
  const [pendingSubmissionPageSize] = useState(20);
  const [pendingSubmissionTotal, setPendingSubmissionTotal] = useState(0);
  const [isPendingSubmissionLoading, setIsPendingSubmissionLoading] = useState(false);
  const [isPendingSubmissionCollapsed, setIsPendingSubmissionCollapsed] = useState(false);
  const [showAllPending, setShowAllPending] = useState(false);

  // Pending Approval state
  const [pendingApprovalRows, setPendingApprovalRows] = useState<any>({ entries: null });
  const [pendingApprovalPage, setPendingApprovalPage] = useState(1);
  const [pendingApprovalPageSize] = useState(20);
  const [pendingApprovalTotal, setPendingApprovalTotal] = useState(0);
  const [isPendingApprovalLoading, setIsPendingApprovalLoading] = useState(false);
  const [isPendingApprovalCollapsed, setIsPendingApprovalCollapsed] = useState(false);

  // History state
  const [historyRows, setHistoryRows] = useState<any>({ entries: null });
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(20);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isHistoryCollapsed, setIsHistoryCollapsed] = useState(false);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Column definitions
  const pendingSubmissionColumns = useMemo(() => [
    { field: 'caseId', name: 'Case ID', width: '75px', template: (e: any) => `<a href="#${e.id}">${e.caseId}</a>` },
    { field: 'natureOfCallValues', name: 'Incident Nature', width: '175px', template: (e: any) => displayNocValuesForGridTemplate(e) },
    { field: 'callLoc', name: 'Location', width: '175px' },
    { field: 'authorName', name: 'Author', width: '80px' },
  ], []);

  const pendingApprovalColumns = useMemo(() => [
    { field: 'caseId', name: 'Case ID', width: '75px', template: (e: any) => `<a href="#${e.id}">${e.caseId}</a>` },
    { field: 'incidentOccurredFrom', name: 'Date/Time', width: '120px', template: (e: any) => e.incidentOccurredFrom ? moment(e.incidentOccurredFrom).format('MM/DD/YYYY HH:mm') : '' },
    { field: 'natureOfCallValues', name: 'Incident Nature', width: '175px', template: (e: any) => displayNocValuesForGridTemplate(e) },
    { field: 'callLoc', name: 'Location', width: '175px' },
    { field: 'authorName', name: 'Author', width: '80px' },
  ], []);

  const historyColumns = useMemo(() => [
    {
      field: 'caseId', name: 'Case ID', width: '75px',
      template: (e: any) => canCurrentUserAccessCaseReport(e.author)
        ? `<a href="#${e.id}">${e.caseId}</a>`
        : e.caseId
    },
    { field: 'incidentOccurredFrom', name: 'Date/Time', width: '120px', template: (e: any) => e.incidentOccurredFrom ? moment(e.incidentOccurredFrom).format('MM/DD/YYYY HH:mm') : '' },
    { field: 'natureOfCallValues', name: 'Incident Nature', width: '175px', template: (e: any) => displayNocValuesForGridTemplate(e) },
    { field: 'callLoc', name: 'Location', width: '175px' },
    { field: 'authorName', name: 'Author', width: '80px' },
    {
      field: 'pdf', name: 'PDF', width: '50px',
      template: (e: any) => `<a href="#pdf-${e.id}" title="Download PDF"><i class="fa fa-download"></i></a>`
    },
  ], []);

  // Row actions
  const pendingSubmissionRowActions = useMemo(() => [
    { name: 'View', icon: 'fa-eye', event: 'view', class: 'btn-primary', mobileOnly: true },
    { name: 'Delete', icon: 'fa-trash', event: 'delete', class: 'btn-danger', iconOnly: true },
  ], []);

  const pendingApprovalRowActions = useMemo(() => [
    { name: 'View', icon: 'fa-eye', event: 'view', class: 'btn-primary' },
  ], []);

  const historyRowActions = useMemo(() => [
    { name: 'View', icon: 'fa-eye', event: 'view', class: 'btn-primary' },
    { name: 'PDF', icon: 'fa-download', event: 'pdf', class: 'btn-secondary' },
  ], []);

  // Navigation helpers
  const viewReport = useCallback((reportId: number) => {
    navigateToCaseReport(reportId);
  }, []);

  const viewHistoryReport = useCallback((reportId: number) => {
    navigateToCaseReport(reportId, true, true);
  }, []);

  const downloadPdf = useCallback((caseReportId: number) => {
    api.getCaseReportPdfUrl(caseReportId, (successResult: any) => {
      if (successResult.uploaded && successResult.signedURL) {
        openPage(successResult.signedURL);
      } else {
        App.showPopupNotification('Error downloading Case Report PDF. It may still be generating - please try again in 15 seconds.', 'error');
      }
    }, () => {
      App.showPopupNotification('Error downloading Case Report PDF.', 'error');
    });
  }, []);

  const onNewReport = useCallback(() => {
    navigateToNewCaseReport();
  }, []);

  // Load functions
  const loadPendingSubmission = useCallback((pg: number, showAll: boolean) => {
    setIsPendingSubmissionLoading(true);
    CaseRpt.showAllPendingReports = showAll;

    api.getReportsPendingFile(showAll, pg, String(pendingSubmissionPageSize), (result: any) => {
      const total = result.records || 0;
      const reports = (result.rows || []).map((r: any) => new PendingCaseReport(r));
      setPendingSubmissionTotal(total);
      setPendingSubmissionRows({ entries: reports });
      setIsPendingSubmissionLoading(false);
    });
  }, [pendingSubmissionPageSize]);

  const loadPendingApproval = useCallback((pg: number) => {
    setIsPendingApprovalLoading(true);

    api.getReportsPendingApproval(pg, String(pendingApprovalPageSize), (result: any) => {
      const total = result.records || 0;
      const reports = result.rows || [];
      setPendingApprovalTotal(total);
      setPendingApprovalRows({ entries: reports });
      setIsPendingApprovalLoading(false);
    });
  }, [pendingApprovalPageSize]);

  const loadHistory = useCallback((pg: number) => {
    setIsHistoryLoading(true);

    api.getReportsHistory(pg, String(historyPageSize), (result: any) => {
      const total = result.records || 0;
      const reports = result.rows || [];
      setHistoryTotal(total);
      setHistoryRows({ entries: reports });
      setIsHistoryLoading(false);
    });
  }, [historyPageSize]);

  // Initial load
  useEffect(() => {
    loadPendingSubmission(1, false);
    loadPendingApproval(1);
    if (canAccessAnyApproved) {
      loadHistory(1);
    }
  }, [loadPendingSubmission, loadPendingApproval, loadHistory, canAccessAnyApproved]);

  // Pager objects
  const pendingSubmissionPager = useMemo(() => {
    const totalPages = Math.ceil(pendingSubmissionTotal / pendingSubmissionPageSize) || 1;
    return {
      curPage: pendingSubmissionPage,
      numPages: totalPages,
      records: pendingSubmissionTotal,
      pageSize: pendingSubmissionPageSize,
      isVisible: totalPages > 1,
    };
  }, [pendingSubmissionPage, pendingSubmissionTotal, pendingSubmissionPageSize]);

  const pendingApprovalPager = useMemo(() => {
    const totalPages = Math.ceil(pendingApprovalTotal / pendingApprovalPageSize) || 1;
    return {
      curPage: pendingApprovalPage,
      numPages: totalPages,
      records: pendingApprovalTotal,
      pageSize: pendingApprovalPageSize,
      isVisible: totalPages > 1,
    };
  }, [pendingApprovalPage, pendingApprovalTotal, pendingApprovalPageSize]);

  const historyPager = useMemo(() => {
    const totalPages = Math.ceil(historyTotal / historyPageSize) || 1;
    return {
      curPage: historyPage,
      numPages: totalPages,
      records: historyTotal,
      pageSize: historyPageSize,
      isVisible: totalPages > 1,
    };
  }, [historyPage, historyTotal, historyPageSize]);

  // Event handlers
  const handlePendingSubmissionRowAction = useCallback((event: { action: string; row: any }) => {
    if (event.action === 'view' && event.row.id) {
      viewReport(event.row.id);
    } else if (event.action === 'delete') {
      setReportToDelete(event.row);
      setShowDeleteModal(true);
    }
  }, [viewReport]);

  const handlePendingApprovalRowAction = useCallback((event: { action: string; row: any }) => {
    if (event.action === 'view' && event.row.id) {
      viewReport(event.row.id);
    }
  }, [viewReport]);

  const handleHistoryRowAction = useCallback((event: { action: string; row: any }) => {
    if (event.action === 'view' && event.row.id) {
      viewHistoryReport(event.row.id);
    } else if (event.action === 'pdf' && event.row.id) {
      downloadPdf(event.row.id);
    }
  }, [viewHistoryReport, downloadPdf]);

  const handlePendingCellClick = useCallback((event: { row: any; field: string }) => {
    if (event.field === 'caseId' && event.row.id) {
      viewReport(event.row.id);
    }
  }, [viewReport]);

  const handleHistoryCellClick = useCallback((event: { row: any; field: string }) => {
    if (event.field === 'caseId' && event.row.id) {
      viewHistoryReport(event.row.id);
    } else if (event.field === 'pdf' && event.row.id) {
      downloadPdf(event.row.id);
    }
  }, [viewHistoryReport, downloadPdf]);

  const handleDeleteConfirm = useCallback(() => {
    if (!reportToDelete) return;
    setIsDeleting(true);
    api.deleteReport(reportToDelete.id, () => {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setReportToDelete(null);
      loadPendingSubmission(pendingSubmissionPage, showAllPending);
    });
  }, [reportToDelete, loadPendingSubmission, showAllPending, pendingSubmissionPage]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
    setReportToDelete(null);
  }, []);

  const handleTogglePending = useCallback(() => {
    const newShowAll = !showAllPending;
    setShowAllPending(newShowAll);
    setPendingSubmissionPage(1);
    loadPendingSubmission(1, newShowAll);
  }, [showAllPending, loadPendingSubmission]);

  const handlePendingSubmissionPageChange = useCallback((pg: number) => {
    setPendingSubmissionPage(pg);
    loadPendingSubmission(pg, showAllPending);
  }, [loadPendingSubmission, showAllPending]);

  const handlePendingApprovalPageChange = useCallback((pg: number) => {
    setPendingApprovalPage(pg);
    loadPendingApproval(pg);
  }, [loadPendingApproval]);

  const handleHistoryPageChange = useCallback((pg: number) => {
    setHistoryPage(pg);
    loadHistory(pg);
  }, [loadHistory]);

  const scrollHeaderIntoView = useCallback((e: React.MouseEvent) => {
    const header = e.currentTarget as HTMLElement;
    if (header) {
      setTimeout(() => {
        header.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 0);
    }
  }, []);

  const deleteConfirmMessage = useMemo(() => {
    const caseId = reportToDelete ? reportToDelete.caseId : '';
    return `<p>Are you sure you want to delete case report <strong>#${caseId}</strong>?</p><p>This action cannot be undone.</p>`;
  }, [reportToDelete]);

  return (
    <div className={`case-reports-page ${theme}`} style={{ paddingBottom: '15px' }}>
      {/* Page Header */}
      <div className="case-reports-page-header">
        <h1>Case Reports</h1>
        <div className="case-reports-page-header-actions">
          {showToggleButton && (
            <button className="btn-header-action" onClick={handleTogglePending}>
              {showAllPending ? 'View My Reports' : 'View All Reports'}
            </button>
          )}
          <button className="btn-new" onClick={onNewReport}>
            <i className="fa fa-plus"></i> New Report
          </button>
        </div>
      </div>

      {/* Pending Submission */}
      <div className="case-reports-section">
        <div
          className="case-reports-header"
          onClick={(e) => {
            setIsPendingSubmissionCollapsed(!isPendingSubmissionCollapsed);
            scrollHeaderIntoView(e);
          }}
        >
          <h3>
            <i className={`fa section-collapse-icon ${isPendingSubmissionCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'}`}></i>
            Pending Submission
            {isPendingSubmissionCollapsed && pendingSubmissionTotal > 0 && (
              <span className="section-count">({pendingSubmissionTotal})</span>
            )}
          </h3>
        </div>
        {!isPendingSubmissionCollapsed && (
          <div className="case-reports-grid-wrapper">
            <KstDataGrid
              columns={pendingSubmissionColumns}
              rows={pendingSubmissionRows}
              pager={pendingSubmissionPager}
              rowactions={pendingSubmissionRowActions}
              loading={isPendingSubmissionLoading}
              showcheckbox={false}
              showheader={false}
              mobiletitlefield="caseId"
              onRowAction={handlePendingSubmissionRowAction}
              onCellClick={handlePendingCellClick}
              onGoToPage={handlePendingSubmissionPageChange}
            />
          </div>
        )}
      </div>

      {/* Pending Approval */}
      <div className="case-reports-section">
        <div
          className="case-reports-header"
          onClick={(e) => {
            setIsPendingApprovalCollapsed(!isPendingApprovalCollapsed);
            scrollHeaderIntoView(e);
          }}
        >
          <h3>
            <i className={`fa section-collapse-icon ${isPendingApprovalCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'}`}></i>
            Pending Approval
            {isPendingApprovalCollapsed && pendingApprovalTotal > 0 && (
              <span className="section-count">({pendingApprovalTotal})</span>
            )}
          </h3>
        </div>
        {!isPendingApprovalCollapsed && (
          <div className="case-reports-grid-wrapper">
            <KstDataGrid
              columns={pendingApprovalColumns}
              rows={pendingApprovalRows}
              pager={pendingApprovalPager}
              rowactions={pendingApprovalRowActions}
              loading={isPendingApprovalLoading}
              showcheckbox={false}
              showheader={false}
              mobiletitlefield="caseId"
              mobileactionsonly={true}
              onRowAction={handlePendingApprovalRowAction}
              onCellClick={handlePendingCellClick}
              onGoToPage={handlePendingApprovalPageChange}
            />
          </div>
        )}
      </div>

      {/* History */}
      {canAccessAnyApproved && (
        <div className="case-reports-section">
          <div
            className="case-reports-header"
            onClick={(e) => {
              setIsHistoryCollapsed(!isHistoryCollapsed);
              scrollHeaderIntoView(e);
            }}
          >
            <h3>
              <i className={`fa section-collapse-icon ${isHistoryCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'}`}></i>
              History
              {isHistoryCollapsed && historyTotal > 0 && (
                <span className="section-count">({historyTotal})</span>
              )}
            </h3>
          </div>
          {!isHistoryCollapsed && (
            <div className="case-reports-grid-wrapper">
              <KstDataGrid
                columns={historyColumns}
                rows={historyRows}
                pager={historyPager}
                rowactions={historyRowActions}
                loading={isHistoryLoading}
                showcheckbox={false}
                showheader={false}
                mobiletitlefield="caseId"
                mobileactionsonly={true}
                onRowAction={handleHistoryRowAction}
                onCellClick={handleHistoryCellClick}
                onGoToPage={handleHistoryPageChange}
              />
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={showDeleteModal}
        title="Delete Report"
        messageHtml={deleteConfirmMessage}
        confirmText="Delete"
        confirmClass="danger"
        confirmDisabled={isDeleting}
        busy={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
}

export default CaseReportListScreen;
